import { BoxShadow, Color, CssVariable } from '@wix/css-property-parser';

/**
 * Extracts the opacity value from a box-shadow CSS value
 * Returns 0 for invisible shadows, 1 for fully opaque, or the alpha value
 *
 * @param shadowValue - The box-shadow CSS value (e.g., "0px 4px 8px rgba(0,0,0,0.5)")
 * @returns Opacity value between 0 and 1
 */
export const extractShadowOpacity = (
  shadowValue: string | undefined,
): number => {
  // Handle empty or undefined values
  if (!shadowValue || shadowValue.trim() === '') {
    return 0;
  }

  const trimmedValue = shadowValue.trim();

  // CSS variables are considered to have opacity (we can't know the actual value)
  // Default to 1 (opaque) to show the shadow action
  if (trimmedValue.startsWith('var(')) {
    return 1;
  }

  // Parse the box-shadow value
  const parsed = BoxShadow.parse(trimmedValue);

  if (!parsed) {
    // Fallback to regex for edge cases
    return extractOpacityFromRegex(trimmedValue);
  }

  // Handle CSS variable result
  if ('type' in parsed) {
    if (parsed.type === 'variable') {
      return 1;
    }
    if (parsed.type === 'keyword') {
      return 0;
    }
  }

  // Handle shadows array
  if ('shadows' in parsed && parsed.shadows && parsed.shadows.length > 0) {
    const firstShadow = parsed.shadows[0];

    // Extract color string using Color.toCSSValue
    if ('color' in firstShadow && firstShadow.color) {
      const colorString = Color.toCSSValue(firstShadow.color);

      if (colorString) {
        // Parse the color string to extract opacity
        return extractOpacityFromColorString(colorString);
      }
    }
  }

  // No color found - assume opaque
  return 1;
};

/**
 * Extracts opacity from a color string
 */
const extractOpacityFromColorString = (colorString: string): number => {
  // Handle transparent keyword
  if (colorString.toLowerCase() === 'transparent') {
    return 0;
  }

  // For reliability, use regex first to extract alpha from common formats
  // The CSS parser may normalize colors in unexpected ways
  const regexOpacity = extractOpacityFromRegex(colorString);

  // If regex found an alpha value < 1, use it
  if (regexOpacity < 1) {
    return regexOpacity;
  }

  // Otherwise parse to check if it's actually transparent
  const color = Color.parse(colorString);

  if (!color) {
    return 1;
  }

  // Handle variable
  if ('type' in color && color.type === 'variable') {
    if (color.type === 'variable') {
      return 1;
    }

    return 1;
  }

  // Check for alpha in parsed color
  if ('alpha' in color && typeof color.alpha === 'number') {
    return color.alpha;
  }
  // No alpha channel - fully opaque
  return 1;
};

/**
 * Fallback regex-based opacity extraction
 * Used when CSS parser fails or for edge cases
 */
const extractOpacityFromRegex = (shadowValue: string): number => {
  // Check for transparent keyword
  if (/transparent/i.test(shadowValue)) {
    return 0;
  }

  // Extract rgba opacity
  const rgbaMatch = shadowValue.match(/rgba\s*\([^)]*,\s*([0-9.]+)\s*\)/i);
  if (rgbaMatch) {
    const alpha = parseFloat(rgbaMatch[1]);
    return isNaN(alpha) ? 1 : alpha;
  }

  // Extract hsla opacity
  const hslaMatch = shadowValue.match(/hsla\s*\([^)]*,\s*([0-9.]+)\s*\)/i);
  if (hslaMatch) {
    const alpha = parseFloat(hslaMatch[1]);
    return isNaN(alpha) ? 1 : alpha;
  }

  // Check for 8-digit hex with alpha (#RRGGBBAA)
  const hexAlphaMatch = shadowValue.match(/#[0-9a-f]{6}([0-9a-f]{2})/i);
  if (hexAlphaMatch) {
    const alphaHex = hexAlphaMatch[1];
    const alpha = parseInt(alphaHex, 16) / 255;
    return alpha;
  }

  // Default to opaque if no transparency indicators found
  return 1;
};
